import { modal } from 'components/Modals/ModalManager';
import { Routine } from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import { stores } from 'state/stores';
import API from 'utils/API';
import { fireAndForget } from 'utils/async';
import { sendEvent } from 'utils/events';
import { SupersetGroups } from 'components/ExerciseSummary/types';
import { exercisesToSupersetGroups } from '../../components/ExerciseSummary/utils';
import { log } from 'utils/log';
import { copyToClipboard } from 'utils/clipboard';

interface Props {
  routine?: Routine;
  isAuthenticated: boolean;
}

export class RoutineDetailViewModel {
  routine?: Routine;
  isAuthenticated: boolean;
  isError: boolean = false;

  constructor({ routine, isAuthenticated }: Props) {
    log('RoutineDetailViewModel constructor');

    makeAutoObservable(this);

    this.isAuthenticated = isAuthenticated;

    if (routine) {
      this.routine = routine;
    } else {
      this.isError = true;
    }
  }

  get existingUserSaveButtonTitle(): string {
    return this.isRoutineSaved ? 'Saved' : 'Save Routine';
  }

  get isRoutineSaved(): boolean {
    const routineId = this.routine?.id;
    if (!routineId) return false;

    return !!stores.routines.routines.find(r => r.parent_routine_id === routineId);
  }

  onExistingUserSaveRoutine = async () => {
    const routineId = this.routine?.id;
    if (!routineId) return;

    if (!stores.subscription.isPro && stores.routines.routines.length >= 4) {
      modal.paywall({
        type: 'routine-limit',
        onCancel: () => null,
      });
      return;
    }

    const save = () => {
      return toast.promise(
        (async () => {
          await API.postRoutineCopy({
            routineId,
            ignoreValues: true,
            index: 0,
            folderId: null,
          });
          await fireAndForget([stores.routines.fetch()]);
        })(),
        {
          loading: 'Saving routine...',
          success: 'Routine saved!',
          error: 'Oops something went wrong',
        },
      );
    };

    if (this.isRoutineSaved) {
      modal.confirmation({
        title: `Save Routine Again?`,
        subtitle: `You've already saved this routine. Would you like to make another copy?`,
        submitButtonTitle: 'Yes!',
        onConfirm: save,
      });
    } else {
      save();
    }
  };

  onCopyUrl = () => {
    sendEvent('routineDetail_copyLink', { routineId: this.routineId });
    copyToClipboard(window.location.href);
    toast.success('Copied to clipboard');
  };

  onDeleteRoutine = () => {
    if (!this.routine) return;

    modal.desctructiveConfirmation({
      title: `Delete '${this.routine.title}' Routine?`,
      subtitle: `Are you sure you want to delete this routine? It'll be gone forever.`,
      submitButtonTitle: 'Delete Routine',
      onConfirm: async () => {
        await toast.promise(
          (async () => {
            await API.deleteRoutine(this.routineId);
            await fireAndForget([stores.routines.fetch()]);
          })(),
          {
            loading: 'Deleting routine...',
            success: 'Routine deleted!',
            error: 'Failed to delete routine',
          },
          {
            success: {
              icon: '🔥',
            },
          },
        );

        sendEvent('routineDetail_deleteRoutine_complete');
      },
    });
  };

  get routineId() {
    return this.routine?.id || '';
  }

  get isUsersRoutine() {
    if (stores.auth.isAuthenticated) {
      return stores.account.username === this.routine?.username;
    } else {
      return false;
    }
  }

  get title(): string {
    return this.routine?.title || '';
  }

  get routineOwnerProfilePic(): string | undefined {
    return this.routine?.profile_pic ?? undefined;
  }

  get username(): string {
    return this.routine?.username || '';
  }

  get supersetGroups(): SupersetGroups {
    if (!this.routine) return [];

    return exercisesToSupersetGroups(this.routine);
  }
}
